/**
 * User Preferences Types and Interfaces for JOYOUS Admin Dashboard
 * Defines comprehensive user preference system for language, date formatting, and number systems
 */

export type Language = 'en' | 'ar';

export type DateFormat = 'gregorian' | 'hijri' | 'mixed';

export type NumberSystem = 'english' | 'arabic';

export interface UserPreferences {
  language: Language;
  dateFormat: DateFormat;
  numberSystem: NumberSystem;
  timezone: string;
  currency: string;
  theme: 'light' | 'dark' | 'auto';
}

export interface UserPreferencesState {
  preferences: UserPreferences;
  isLoading: boolean;
  isSaving: boolean;
  lastSaved: Date | null;
  error: string | null;
}

export interface UserPreferencesContext {
  state: UserPreferencesState;
  preferences: UserPreferences;
  updatePreference: <K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K]
  ) => Promise<void>;
  updatePreferences: (updates: Partial<UserPreferences>) => Promise<void>;
  resetToDefaults: () => Promise<void>;
  loadPreferences: () => Promise<void>;
  savePreferences: () => Promise<void>;
  refreshPreferences: () => Promise<void>;
}

export interface FormattingOptions {
  locale?: string;
  language?: Language;
  dateFormat?: DateFormat;
  numberSystem?: NumberSystem;
  timezone?: string;
  currency?: string;
}

export interface DateFormattingOptions {
  style?: 'short' | 'medium' | 'long' | 'full';
  includeTime?: boolean;
  use24Hour?: boolean;
  timezone?: string;
  dateFormat?: DateFormat;
}

export interface NumberFormattingOptions {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  useGrouping?: boolean;
  numberSystem?: NumberSystem;
}

export interface CurrencyFormattingOptions {
  currency?: string;
  locale?: string;
  numberSystem?: NumberSystem;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

// Default preferences with English defaults as specified
export const DEFAULT_PREFERENCES: UserPreferences = {
  language: 'en',
  dateFormat: 'gregorian',
  numberSystem: 'english',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  currency: 'SAR',
  theme: 'auto'
};

// Preference validation schemas
export const PREFERENCES_VALIDATION = {
  language: ['en', 'ar'] as const,
  dateFormat: ['gregorian', 'hijri', 'mixed'] as const,
  numberSystem: ['english', 'arabic'] as const,
  theme: ['light', 'dark', 'auto'] as const
};

// Helper type for preference keys
export type PreferenceKey = keyof UserPreferences;

// Helper type for preference update payload
export type PreferenceUpdatePayload = Partial<UserPreferences>;

// Interface for API responses
export interface UserPreferencesResponse {
  success: boolean;
  data: UserPreferences;
  message?: string;
}

// Interface for preference change events
export interface PreferenceChangeEvent {
  key: PreferenceKey;
  oldValue: unknown;
  newValue: unknown;
  timestamp: Date;
}

// Preference change listener type
export type PreferenceChangeListener = (event: PreferenceChangeEvent) => void;

// Storage configuration
export interface PreferencesStorageConfig {
  storageKey: string;
  storageType: 'localStorage' | 'sessionStorage';
  encryptionEnabled: boolean;
  syncWithServer: boolean;
  syncInterval: number; // in minutes
}

export const DEFAULT_STORAGE_CONFIG: PreferencesStorageConfig = {
  storageKey: 'joyous-admin-preferences',
  storageType: 'localStorage',
  encryptionEnabled: false,
  syncWithServer: true,
  syncInterval: 5
};

// Preference migration types
export interface PreferenceMigration {
  version: number;
  description: string;
  migrate: (oldPreferences: any) => UserPreferences;
}

// Note: All types and interfaces are already exported above with individual export statements
// This provides easy importing: import { UserPreferences, UserPreferencesState } from './types/user-preferences'